import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { fmtDate } from '../../utils/formatters'

export default function EmailsPage() {
  var { user, profile } = useAuth()
  var navigate = useNavigate()
  var canEdit = profile && ['owner', 'office_staff', 'project_manager'].includes(profile.role)
  var [activeTab, setActiveTab] = useState('email')

  return (
    <div style={st.container}>
      <div style={st.header}>
        <div>
          <div style={st.title}>INBOX</div>
          <div style={st.subtitle}>Parse emails and text messages into opportunities and bids</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        {[['email', 'EMAIL PARSER'], ['text', 'TEXT PARSER']].map(function(t) {
          return <button key={t[0]} onClick={function() { setActiveTab(t[0]) }} style={{ ...st.tabBtn, ...(activeTab === t[0] ? st.tabBtnActive : {}) }}>{t[1]}</button>
        })}
      </div>
      {activeTab === 'email' && <EmailParserTab user={user} canEdit={canEdit} />}
      {activeTab === 'text' && <TextParserTab user={user} canEdit={canEdit} navigate={navigate} />}
    </div>
  )
}

// ========== EMAIL PARSER TAB ==========
function EmailParserTab({ user, canEdit }) {
  var [businessId, setBusinessId] = useState('riverbend')
  var [emailText, setEmailText] = useState('')
  var [parsing, setParsing] = useState(false)
  var [parsed, setParsed] = useState(null)
  var [editedGC, setEditedGC] = useState(null)
  var [editedProjects, setEditedProjects] = useState([])
  var [saving, setSaving] = useState(false)
  var [error, setError] = useState('')
  var [success, setSuccess] = useState('')
  var [history, setHistory] = useState([])
  var [historyLoading, setHistoryLoading] = useState(true)

  var fetchHistory = useCallback(async function() {
    setHistoryLoading(true)
    var { data } = await supabase.from('parsed_emails').select('*').order('created_at', { ascending: false }).limit(50)
    setHistory(data || [])
    setHistoryLoading(false)
  }, [])

  useEffect(function() { fetchHistory() }, [fetchHistory])

  var handleParse = async function() {
    if (!emailText.trim()) return
    setParsing(true); setError(''); setSuccess(''); setParsed(null)
    try {
      var resp = await fetch('/api/parse-bid-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email_text: emailText, business_id: businessId }) })
      var data = await resp.json()
      if (data.success && data.data) {
        setParsed(data.data)
        setEditedGC(Object.assign({}, data.data.gc || { company_name: '', contact_name: '', email: '', phone: '' }))
        setEditedProjects((data.data.projects || []).map(function(p) { return Object.assign({}, p) }))
      } else { setError(data.error || 'Failed to parse email.') }
    } catch (err) { setError('Parse failed: ' + err.message) }
    setParsing(false)
  }

  var updateProject = function(idx, field, value) {
    setEditedProjects(function(prev) { var copy = prev.map(function(p) { return Object.assign({}, p) }); copy[idx][field] = value; return copy })
  }

  var removeProject = function(idx) {
    setEditedProjects(function(prev) { return prev.filter(function(_, i) { return i !== idx }) })
  }

  var handleSaveAll = async function() {
    if (!editedGC || editedProjects.length === 0) return
    setSaving(true); setError(''); setSuccess('')
    try {
      var gcId = null
      if (editedGC.company_name) {
        var cleanName = (editedGC.company_name || '').replace(/[.,]+$/g, '').replace(/\bLLC\.?\b/gi, '').replace(/\bInc\.?\b/gi, '').replace(/\bCo\.?\b/gi, '').trim()
        var cleanAlpha = cleanName.toLowerCase().replace(/[^a-z0-9]/g, '')
        var { data: allGCs } = await supabase.from('gc_contacts').select('id, company_name')
        var match = null
        if (allGCs && allGCs.length > 0) {
          match = allGCs.find(function(gc) {
            var gcAlpha = (gc.company_name || '').replace(/\bLLC\.?\b/gi, '').replace(/\bInc\.?\b/gi, '').replace(/\bCo\.?\b/gi, '').toLowerCase().replace(/[^a-z0-9]/g, '')
            return gcAlpha === cleanAlpha || gcAlpha.includes(cleanAlpha) || cleanAlpha.includes(gcAlpha)
          })
        }
        if (match) {
          gcId = match.id
          var gcUpdates = {}
          if (editedGC.contact_name) gcUpdates.contact_name = editedGC.contact_name
          if (editedGC.email) gcUpdates.email = editedGC.email
          if (editedGC.phone) gcUpdates.phone = editedGC.phone
          if (Object.keys(gcUpdates).length > 0) await supabase.from('gc_contacts').update(gcUpdates).eq('id', gcId)
        } else {
          var { data: newGC } = await supabase.from('gc_contacts').insert({ company_name: editedGC.company_name, contact_name: editedGC.contact_name || '', email: editedGC.email || '', phone: editedGC.phone || '', business_id: businessId }).select().single()
          if (newGC) gcId = newGC.id
        }
      }
      var created = 0
      for (var i = 0; i < editedProjects.length; i++) {
        var proj = editedProjects[i]
        if (proj.estimator && gcId) {
          var { data: existEst } = await supabase.from('gc_estimators').select('id').eq('gc_contact_id', gcId).ilike('name', proj.estimator).limit(1)
          if (!existEst || existEst.length === 0) await supabase.from('gc_estimators').insert({ gc_contact_id: gcId, name: proj.estimator, email: proj.estimator_email || '', phone: proj.estimator_phone || '', role: 'estimator' })
        }
        var { data: existing } = await supabase.from('opportunities').select('id').ilike('project_name', '%' + (proj.project_name || '') + '%').eq('business_id', businessId).limit(1)
        if (existing && existing.length > 0) continue
        await supabase.from('opportunities').insert({ business_id: businessId, project_name: proj.project_name || 'Untitled', gc_contact_id: gcId, gc_name: editedGC.company_name || '', estimator: proj.estimator || '', estimator_email: proj.estimator_email || '', estimator_phone: proj.estimator_phone || '', due_date: proj.due_date || null, plan_links: proj.plan_links && proj.plan_links.length > 0 ? proj.plan_links : [], site_address: proj.site_address || '', notes: proj.scope_notes || proj.notes || '', status: 'open', source_email: emailText.substring(0, 500) })
        created++
      }
      await supabase.from('parsed_emails').insert({ business_id: businessId, sender_name: editedGC.contact_name || '', sender_email: editedGC.email || '', raw_text: emailText.substring(0, 5000), parsed_json: { gc: editedGC, projects: editedProjects }, gc_contact_id: gcId, gc_name: editedGC.company_name || '', status: 'processed', opportunities_created: created, created_by: user ? user.id : null, processed_at: new Date().toISOString() })
      if (created > 0) { setSuccess(created + ' opportunit' + (created === 1 ? 'y' : 'ies') + ' created!'); setParsed(null); setEditedGC(null); setEditedProjects([]); setEmailText(''); fetchHistory() }
      else { setError('All projects already exist (duplicates skipped).') }
    } catch (err) { setError('Save failed: ' + err.message) }
    setSaving(false)
  }

  return (
    <div>
      <div style={st.card}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <div style={st.fieldLabel}>BUSINESS</div>
          <BizSelector value={businessId} onChange={setBusinessId} />
        </div>
        {!parsed ? (
          <div>
            <div style={st.fieldLabel}>PASTE BID INVITATION EMAIL</div>
            <textarea value={emailText} onChange={function(e) { setEmailText(e.target.value) }} style={st.textarea} placeholder="Paste the full email text here including subject, sender info, project details, plan links..." rows={14} />
            {error && <div style={st.error}>{error}</div>}
            {success && <div style={st.success}>{success}</div>}
            <button onClick={handleParse} disabled={parsing || !emailText.trim() || !canEdit} style={{ ...st.primaryBtn, opacity: emailText.trim() && canEdit ? 1 : 0.4 }}>{parsing ? 'PARSING...' : 'PARSE EMAIL'}</button>
          </div>
        ) : (
          <div>
            <div style={st.parseNote}>Review and edit the parsed results below, then create opportunities.</div>
            <div style={st.gcCard}>
              <div style={st.sectionLabel}>GENERAL CONTRACTOR</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><div style={st.fieldLabel}>COMPANY NAME</div><input value={editedGC.company_name || ''} onChange={function(e) { setEditedGC(Object.assign({}, editedGC, { company_name: e.target.value })) }} style={st.input} /></div>
                <div><div style={st.fieldLabel}>CONTACT (SENDER)</div><input value={editedGC.contact_name || ''} onChange={function(e) { setEditedGC(Object.assign({}, editedGC, { contact_name: e.target.value })) }} style={st.input} /></div>
                <div><div style={st.fieldLabel}>EMAIL</div><input value={editedGC.email || ''} onChange={function(e) { setEditedGC(Object.assign({}, editedGC, { email: e.target.value })) }} style={st.input} /></div>
                <div><div style={st.fieldLabel}>PHONE</div><input value={editedGC.phone || ''} onChange={function(e) { setEditedGC(Object.assign({}, editedGC, { phone: e.target.value })) }} style={st.input} /></div>
              </div>
            </div>
            <div style={st.sectionLabel}>PROJECTS FOUND ({editedProjects.length})</div>
            {editedProjects.map(function(proj, idx) {
              return (
                <div key={idx} style={st.projCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', color: '#b48c3c' }}>PROJECT {idx + 1}</div>
                    <button onClick={function() { removeProject(idx) }} style={st.removeBtn}>REMOVE</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ gridColumn: '1 / -1' }}><div style={st.fieldLabel}>PROJECT NAME</div><input value={proj.project_name || ''} onChange={function(e) { updateProject(idx, 'project_name', e.target.value) }} style={st.input} /></div>
                    <div><div style={st.fieldLabel}>ESTIMATOR</div><input value={proj.estimator || ''} onChange={function(e) { updateProject(idx, 'estimator', e.target.value) }} style={st.input} /></div>
                    <div><div style={st.fieldLabel}>DUE DATE</div><input type="date" value={proj.due_date || ''} onChange={function(e) { updateProject(idx, 'due_date', e.target.value) }} style={st.input} /></div>
                    <div><div style={st.fieldLabel}>ESTIMATOR EMAIL</div><input value={proj.estimator_email || ''} onChange={function(e) { updateProject(idx, 'estimator_email', e.target.value) }} style={st.input} /></div>
                    <div><div style={st.fieldLabel}>ESTIMATOR PHONE</div><input value={proj.estimator_phone || ''} onChange={function(e) { updateProject(idx, 'estimator_phone', e.target.value) }} style={st.input} /></div>
                    <div style={{ gridColumn: '1 / -1' }}><div style={st.fieldLabel}>SITE ADDRESS</div><input value={proj.site_address || ''} onChange={function(e) { updateProject(idx, 'site_address', e.target.value) }} style={st.input} /></div>
                    <div style={{ gridColumn: '1 / -1' }}><div style={st.fieldLabel}>PLAN LINKS (comma separated)</div><input value={(proj.plan_links || []).join(', ')} onChange={function(e) { updateProject(idx, 'plan_links', e.target.value.split(',').map(function(l) { return l.trim() }).filter(Boolean)) }} style={st.input} /></div>
                    <div style={{ gridColumn: '1 / -1' }}><div style={st.fieldLabel}>SCOPE NOTES</div><textarea value={proj.scope_notes || ''} onChange={function(e) { updateProject(idx, 'scope_notes', e.target.value) }} style={{ ...st.input, minHeight: '60px', resize: 'vertical' }} /></div>
                  </div>
                </div>
              )
            })}
            {error && <div style={st.error}>{error}</div>}
            {success && <div style={st.success}>{success}</div>}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={function() { setParsed(null); setEditedGC(null); setEditedProjects([]) }} style={st.backBtn}>BACK</button>
              <button onClick={handleSaveAll} disabled={saving || editedProjects.length === 0} style={{ ...st.primaryBtn, flex: 1 }}>{saving ? 'CREATING...' : 'CREATE OPPORTUNITIES (' + editedProjects.length + ')'}</button>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: '32px' }}>
        <div style={st.sectionLabel}>PARSED EMAIL HISTORY</div>
        {historyLoading ? <div style={st.emptyMsg}>Loading...</div> : history.length === 0 ? <div style={st.emptyMsg}>No emails parsed yet</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {history.map(function(h) {
              var pj = h.parsed_json || {}; var projCount = (pj.projects || []).length
              return (
                <div key={h.id} style={st.historyRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div><span style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>{h.gc_name || 'Unknown GC'}</span><span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginLeft: '12px' }}>{h.sender_name}{h.sender_email ? ' <' + h.sender_email + '>' : ''}</span></div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{ ...st.badge, backgroundColor: h.business_id === 'pools' ? 'rgba(180,140,60,0.15)' : 'rgba(74,140,106,0.15)', color: h.business_id === 'pools' ? '#b48c3c' : '#4a8c6a' }}>{h.business_id === 'pools' ? 'CPP' : 'RBC'}</span>
                      <span style={{ ...st.badge, backgroundColor: 'rgba(74,140,106,0.15)', color: '#4a8c6a' }}>{h.opportunities_created || 0} created</span>
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>{fmtDate(h.created_at)}</span>
                    </div>
                  </div>
                  {projCount > 0 && <div style={{ marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{(pj.projects || []).map(function(p, pi) { return <span key={pi} style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', padding: '2px 8px', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '2px' }}>{p.project_name}</span> })}</div>}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ========== TEXT PARSER TAB ==========
function TextParserTab({ user, canEdit, navigate }) {
  var [businessId, setBusinessId] = useState('pools')
  var [textInput, setTextInput] = useState('')
  var [parsing, setParsing] = useState(false)
  var [parsed, setParsed] = useState(null)
  var [edited, setEdited] = useState(null)
  var [saving, setSaving] = useState(false)
  var [error, setError] = useState('')
  var [success, setSuccess] = useState('')
  var [history, setHistory] = useState([])
  var [historyLoading, setHistoryLoading] = useState(true)

  var fetchHistory = useCallback(async function() {
    setHistoryLoading(true)
    var { data } = await supabase.from('incoming_messages').select('*').order('created_at', { ascending: false }).limit(50)
    setHistory(data || [])
    setHistoryLoading(false)
  }, [])

  useEffect(function() { fetchHistory() }, [fetchHistory])

  var handleParse = async function() {
    if (!textInput.trim()) return
    setParsing(true); setError(''); setSuccess(''); setParsed(null); setEdited(null)
    try {
      var resp = await fetch('/api/parse-text-message', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: textInput, business_id: businessId }) })
      var data = await resp.json()
      if (data.success && data.data) {
        setParsed(data.data)
        setEdited(Object.assign({}, data.data))
      } else { setError(data.error || 'Failed to parse text.') }
    } catch (err) { setError('Parse failed: ' + err.message) }
    setParsing(false)
  }

  var setField = function(k, v) { setEdited(function(prev) { return Object.assign({}, prev, { [k]: v }) }) }

  var handleCreateBid = async function() {
    if (!edited) return
    setSaving(true); setError(''); setSuccess('')
    try {
      var bidType = businessId === 'pools' ? (edited.pool_type || 'above_ground') : (edited.job_type || 'land_clearing')
      var address = [edited.site_address, edited.city, edited.state, edited.zip].filter(Boolean).join(', ')
      var descParts = []
      if (edited.project_description) descParts.push(edited.project_description)
      if (edited.notes) descParts.push(edited.notes)
      if (edited.urgency) descParts.push('Urgency: ' + edited.urgency)

      var { error: bidErr } = await supabase.from('bids').insert({
        business_id: businessId,
        bid_type: bidType,
        client_name: edited.client_name || '',
        client_phone: edited.client_phone || '',
        client_email: edited.client_email || '',
        site_address: address,
        job_name: edited.project_description ? edited.project_description.substring(0, 100) : '',
        description: descParts.join('\n\n'),
        status: 'draft',
        created_by: user ? user.id : null,
      }).select().single()

      if (bidErr) { setError('Failed to create bid: ' + bidErr.message) }
      else {
        setSuccess('Bid created! Redirecting...')
        setParsed(null); setEdited(null); setTextInput('')
        fetchHistory()
        setTimeout(function() { navigate('/bids') }, 1200)
      }
    } catch (err) { setError('Save failed: ' + err.message) }
    setSaving(false)
  }

  var TEXT_FIELDS = [
    ['client_name', 'CLIENT NAME'],
    ['client_phone', 'PHONE'],
    ['client_email', 'EMAIL'],
    ['site_address', 'STREET ADDRESS'],
    ['city', 'CITY'],
    ['state', 'STATE'],
    ['zip', 'ZIP'],
    ['project_description', 'PROJECT DESCRIPTION'],
    ['pool_type', 'POOL TYPE'],
    ['job_type', 'JOB TYPE'],
    ['urgency', 'URGENCY'],
    ['notes', 'NOTES'],
  ]

  return (
    <div>
      <div style={st.card}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <div style={st.fieldLabel}>BUSINESS</div>
          <BizSelector value={businessId} onChange={setBusinessId} />
        </div>

        {!parsed ? (
          <div>
            <div style={st.fieldLabel}>PASTE CUSTOMER TEXT MESSAGES</div>
            <textarea value={textInput} onChange={function(e) { setTextInput(e.target.value) }} style={st.textarea} placeholder="Paste customer text messages here..." rows={14} />
            {error && <div style={st.error}>{error}</div>}
            {success && <div style={st.success}>{success}</div>}
            <button onClick={handleParse} disabled={parsing || !textInput.trim() || !canEdit} style={{ ...st.primaryBtn, opacity: textInput.trim() && canEdit ? 1 : 0.4 }}>{parsing ? 'PARSING...' : 'PARSE TEXT'}</button>
          </div>
        ) : (
          <div>
            <div style={st.parseNote}>Review and edit the parsed results below, then create a bid.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {TEXT_FIELDS.map(function(f) {
                var key = f[0]; var label = f[1]; var val = edited[key] || ''
                var isWide = key === 'project_description' || key === 'notes'
                return (
                  <div key={key} style={isWide ? { gridColumn: '1 / -1' } : {}}>
                    <div style={st.fieldLabel}>{label}</div>
                    {isWide ? (
                      <textarea value={val} onChange={function(e) { setField(key, e.target.value) }} style={{ ...st.input, minHeight: '70px', resize: 'vertical' }} />
                    ) : (
                      <input value={val} onChange={function(e) { setField(key, e.target.value) }} style={st.input} />
                    )}
                  </div>
                )
              })}
            </div>
            {error && <div style={st.error}>{error}</div>}
            {success && <div style={st.success}>{success}</div>}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={function() { setParsed(null); setEdited(null) }} style={st.backBtn}>BACK</button>
              <button onClick={handleCreateBid} disabled={saving} style={{ ...st.primaryBtn, flex: 1, backgroundColor: '#4a8c6a' }}>{saving ? 'CREATING...' : 'CREATE BID'}</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '32px' }}>
        <div style={st.sectionLabel}>TEXT MESSAGE HISTORY</div>
        {historyLoading ? <div style={st.emptyMsg}>Loading...</div> : history.length === 0 ? <div style={st.emptyMsg}>No text messages parsed yet</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {history.map(function(h) {
              var pj = h.parsed_json || {}
              return (
                <div key={h.id} style={st.historyRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>{pj.client_name || h.from_number || 'Unknown'}</span>
                      {pj.client_phone && <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginLeft: '12px' }}>{pj.client_phone}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{ ...st.badge, backgroundColor: h.business_id === 'pools' ? 'rgba(180,140,60,0.15)' : 'rgba(74,140,106,0.15)', color: h.business_id === 'pools' ? '#b48c3c' : '#4a8c6a' }}>{h.business_id === 'pools' ? 'CPP' : 'RBC'}</span>
                      <span style={{ ...st.badge, backgroundColor: 'rgba(74,106,140,0.15)', color: '#4a6a8c' }}>{h.status || 'parsed'}</span>
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>{fmtDate(h.created_at)}</span>
                    </div>
                  </div>
                  {pj.project_description && <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{pj.project_description}</div>}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ========== SHARED COMPONENTS ==========
function BizSelector({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[['pools', 'CP QUALITY POOLS'], ['riverbend', 'RIVERBEND CLEARING']].map(function(b) {
        return <button key={b[0]} onClick={function() { onChange(b[0]) }} style={{ ...st.bizBtn, ...(value === b[0] ? st.bizBtnActive : {}) }}>{b[1]}</button>
      })}
    </div>
  )
}

var st = {
  container: { fontFamily: "'Barlow', sans-serif", maxWidth: '900px' },
  header: { marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '14px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  tabBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer' },
  tabBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  card: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '24px' },
  sectionLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', marginBottom: '12px', marginTop: '20px' },
  fieldLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  textarea: { width: '100%', padding: '14px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow', sans-serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  bizBtn: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  bizBtnActive: { backgroundColor: '#b48c3c', borderColor: '#b48c3c', color: '#0a0f1a' },
  primaryBtn: { width: '100%', padding: '14px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '4px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer', marginTop: '16px' },
  backBtn: { padding: '14px 24px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer' },
  removeBtn: { padding: '4px 10px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '2px', color: '#e05c5c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  error: { backgroundColor: 'rgba(220,60,60,0.1)', border: '1px solid rgba(220,60,60,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#ff6b6b', fontSize: '13px', marginTop: '12px' },
  success: { backgroundColor: 'rgba(74,140,106,0.1)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#4a8c6a', fontSize: '13px', marginTop: '12px' },
  parseNote: { fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginBottom: '16px', lineHeight: 1.5 },
  gcCard: { backgroundColor: 'rgba(74,140,106,0.06)', border: '1px solid rgba(74,140,106,0.15)', borderRadius: '4px', padding: '16px', marginBottom: '16px' },
  projCard: { backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', padding: '16px', marginBottom: '8px' },
  historyRow: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', padding: '14px 16px' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '2px', fontSize: '11px', fontWeight: 600, letterSpacing: '1px', fontFamily: "'Barlow Condensed', sans-serif" },
  emptyMsg: { padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '14px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px' },
}
